import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { components, GET } from '@features/shared/api/public/clients';

export function usePromotedClubs({
  clubIds,
  enabled = true,
}: {
  clubIds: string[];
  enabled?: boolean;
}) {
  return useQuery({
    enabled,
    queryKey: ['/api/v1/public/clubs/promoted', clubIds.join(',')],
    queryFn: async () => {
      return (
        await GET('/api/v1/public/clubs/promoted', {
          params: {
            query: {
              ids: clubIds,
            },
          },
        })
      ).data!;
    },
  });
}

export function useClubsListQuery({
  query,
  enabled = true,
  limit = 20,
}: {
  query?: string;
  enabled?: boolean;
  limit?: number;
}) {
  const result = useInfiniteQuery({
    enabled,
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: components['schemas']['ClubPublicDto'][],
      _allPages,
      lastPageParam: number,
    ) => {
      if (lastPage.length < limit) return;
      return lastPageParam + 1;
    },
    placeholderData: (prev) => prev,
    queryKey: ['/api/v1/public/clubs', query, limit],
    queryFn: async ({ pageParam }) => {
      return (
        await GET('/api/v1/public/clubs', {
          params: {
            query: {
              query,
              limit,
              offset: pageParam * limit,
            },
          },
        })
      ).data!;
    },
  });

  let data: components['schemas']['ClubPublicDto'][] = [];
  result?.data?.pages.forEach((page) => {
    data = data.concat(page);
  });

  return {
    ...result,
    data,
  };
}

export function useTournamentsListQuery({
  clockControl,
  mode,
  query,
  limit = 20,
  enabled = true,
}: {
  clockControl?: components['schemas']['ClockControlType'];
  mode?: components['schemas']['TournamentMode'];
  query?: string;
  limit?: number;
  enabled?: boolean;
}) {
  const result = useInfiniteQuery({
    enabled,
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: components['schemas']['TournamentPublicDto'][],
      _allPages,
      lastPageParam: number,
    ) => {
      if (lastPage.length < limit) return;
      return lastPageParam + 1;
    },
    placeholderData: (prev) => prev,
    queryKey: ['/api/v1/public/tournaments', clockControl, mode, query, limit],
    queryFn: async ({ pageParam }) => {
      return (
        await GET('/api/v1/public/tournaments', {
          params: {
            query: {
              clockControl,
              mode,
              query,
              limit,
              offset: pageParam * limit,
            },
          },
        })
      ).data!;
    },
  });

  let data: components['schemas']['TournamentPublicDto'][] = [];
  result?.data?.pages.forEach((page) => {
    data = data.concat(page);
  });

  return {
    ...result,
    data,
  };
}
